import React, { useCallback, useEffect, useRef } from "react";
import { Editor, EditorUtils, ProseMirror } from "@progress/kendo-react-editor";
import { initProseMirrorDoc, ySyncPlugin } from "y-prosemirror";
import * as Y from "yjs";
import { updatedSchema } from "../../../../editor/editor-schema/schema";
import "../../../../editor/editor.scss";
import { inputRule } from "../../../../editor/editorSettings/inputRuleSettings";
import { keyMapSettings } from "../../../../editor/editorSettings/keyMapSettings";
import { contentStyleSettings } from "../../../../editor/editorSettings/pasteSettings";

const { EditorState, EditorView } = ProseMirror;
const { imageResizing, tableResizing, textHighlight } = EditorUtils;

/**
 * Converts documentState (object with numeric keys) to a Y.Doc update Uint8Array.
 * @param {Object} documentState - Y.Doc update as object (e.g. API documentState).
 * @returns {Uint8Array|null}
 */
const toUpdate = (documentState) => {
  if (!documentState || typeof documentState !== "object" || Object.keys(documentState).length === 0) return null;
  return new Uint8Array(Object.values(documentState));
};

/**
 * VersionPreview – read-only view of ydoc document data (no WebSocket).
 * documentState is a Y.Doc update (full document state); Y.applyUpdate is used to reconstruct the document.
 *
 * @param {Object} props
 * @param {Object} props.versionPreviewMode - Object containing show and versionData properties.
 * @param {string} [props.className] - Optional class for the wrapper.
 * @param {Object} [props.contentStyle] - Optional content style for the editor.
 * @param {Object} [props.versionViewRef] - Optional ref to the version view.
 * @author Abhishek Dhillon
 * @parent DescriptionPopup, DescriptionHistory
 */
const VersionPreview = ({ versionPreviewMode, className = "", contentStyle, versionViewRef }) => {
  const editorRef = useRef(null);
  const ydocRef = useRef(null);
  const { versionData } = versionPreviewMode;
  const inputRuleCallback = useCallback((nodes) => inputRule(nodes), []);

  const handleMount = useCallback(
    (event) => {
      const state = event.viewProps.state;
      const schema = state.schema;
      const mySchema = updatedSchema(schema);
      const ydoc = new Y.Doc({ gc: false });
      const permanentUserData = new Y.PermanentUserData(ydoc);
      const yXmlFragment = ydoc.getXmlFragment("prosemirror");
      ydocRef.current = ydoc;

      const update = toUpdate(versionData?.documentState);
      if (update) Y.applyUpdate(ydoc, update);

      let { doc, mapping } = initProseMirrorDoc(yXmlFragment, mySchema);
      if (!doc || doc.childCount === 0) {
        const defaultParagraph = mySchema.nodes.paragraph.create(null, mySchema.text(" "));
        doc = mySchema.nodes.doc.create(null, defaultParagraph);
      }

      const view = new EditorView(
        { mount: event.dom },
        {
          ...event.viewProps,
          state: EditorState.create({
            doc,
            plugins: [
              textHighlight(),
              ySyncPlugin(yXmlFragment, { mapping, permanentUserData }),
              imageResizing(),
              inputRuleCallback(mySchema?.nodes),
              keyMapSettings("descriptionPopUp", () => {}),
              ...tableResizing(),
              ...state.plugins,
            ],
          }),
        }
      );
      versionViewRef.current = view;
      return view;
    },
    [versionData?.documentState, inputRuleCallback]
  );

  useEffect(() => {
    const ydoc = ydocRef.current;
    const update = toUpdate(versionData?.documentState);
    if (!ydoc || !update) return;
    try {
      Y.applyUpdate(ydoc, update);
    } catch (err) {
      // ignore apply errors
    }
  }, [versionData?.documentState, versionPreviewMode?.key]);

  return (
    <div className={`dt-kendo-editor ${className}`.trim()} key={versionPreviewMode?.key}>
      <Editor
        ref={editorRef}
        defaultEditMode='div'
        onMount={handleMount}
        tools={[]}
        contentStyle={contentStyle}
        className='disable-toolbar'
        onExecute={() => {
          return false;
        }}
      />
    </div>
  );
};

export default VersionPreview;
